import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { QuizQuestion, LessonContent, Difficulty } from '@/types';
import { useToast } from '@/hooks/use-toast';

interface UseAIQuizResult {
  generateQuiz: (difficulty: Difficulty, questionCount: number, topic?: string) => Promise<QuizQuestion[]>;
  isLoading: boolean;
  error: string | null;
}

interface UseAILessonResult {
  generateLesson: (topic: string, difficulty: Difficulty, skipCache?: boolean) => Promise<LessonContent | null>;
  isLoading: boolean;
  loadingStep: string;
  error: string | null;
}

export const useAIQuiz = (): UseAIQuizResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const generateQuiz = useCallback(async (
    difficulty: Difficulty,
    questionCount: number,
    topic?: string
  ): Promise<QuizQuestion[]> => {
    setIsLoading(true);
    setError(null);

    try {
      if (import.meta.env.DEV) console.log('Generating quiz:', { difficulty, questionCount, topic });
      
      const { data, error: functionError } = await supabase.functions.invoke('generate-content', {
        body: {
          type: 'quiz',
          difficulty,
          questionCount,
          topic,
        },
      });

      if (functionError) {
        console.error('Function error:', functionError.message);
        throw new Error(functionError.message || 'Failed to generate quiz');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.questions || !Array.isArray(data.questions)) {
        throw new Error('Invalid quiz response format');
      }

      if (import.meta.env.DEV) console.log('Quiz generated successfully:', data.questions.length, 'questions');
      return data.questions;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate quiz';
      console.error('Quiz generation error:', message);
      setError(message);
      
      toast({
        title: 'Quiz Generation Failed',
        description: message,
        variant: 'destructive',
      });
      
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  return { generateQuiz, isLoading, error };
};

export const useAILesson = (): UseAILessonResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const generateLesson = useCallback(async (
    topic: string,
    difficulty: Difficulty,
    skipCache?: boolean
  ): Promise<LessonContent | null> => {
    setIsLoading(true);
    setError(null);
    setLoadingStep(skipCache ? 'Regenerating fresh content...' : 'Analyzing topic...');

    try {
      if (import.meta.env.DEV) console.log('Generating lesson:', { topic, difficulty, skipCache });
      
      // Simulate step progression for better UX
      setTimeout(() => setLoadingStep('Curating maritime examples...'), 1000);
      setTimeout(() => setLoadingStep('Generating content with Gemini 3 Pro...'), 2500);
      
      const { data, error: functionError } = await supabase.functions.invoke('generate-content', {
        body: {
          type: 'lesson',
          topic,
          difficulty,
          ...(skipCache ? { skipCache: true } : {}),
        },
      });

      if (functionError) {
        console.error('Function error:', functionError.message);
        throw new Error(functionError.message || 'Failed to generate lesson');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.title || !data.sections) {
        throw new Error('Invalid lesson response format');
      }

      if (import.meta.env.DEV) console.log('Lesson generated successfully:', data.title);
      setLoadingStep('');
      return data as LessonContent;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate lesson';
      console.error('Lesson generation error:', message);
      setError(message);
      setLoadingStep('');
      
      toast({
        title: 'Lesson Generation Failed',
        description: message,
        variant: 'destructive',
      });
      
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  return { generateLesson, isLoading, loadingStep, error };
};
